import React, { useState, useEffect, useRef, type KeyboardEvent } from 'react';
import {
  Dialog,
  IconButton,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  Divider,
  Stack,
  Chip,
  InputAdornment,
  OutlinedInput,
  LinearProgress,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { ClearIcon } from '@mui/x-date-pickers';
import { useDebounce } from 'use-debounce';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';
import { colors } from 'theme/mui/colors';
import { ArrowRightIcon } from '../icons/ArrowRightIcon';
import { ArrowUpIcon } from '../icons/ArrowUpIcon';
import { ArrowDownIcon } from '../icons/ArrowDownIcon';
import CaretDownIcon from '../icons/CaretDownIcon';
import { getExistingCasesApi, getNewCasesApi } from 'api/cases/query';
import type { IDatahubCase } from 'api';
import { CaseSearchStatus } from 'constants/enums/status';
import routes from 'app.routes';
import { useLocalization } from 'localization';
import { ORDER_CREATION_PACKAGE_TYPE } from 'constants/ui/status';
import { useStartCreateNewOrder } from 'hooks/services/useStartCreateNewOrder';

export type CaseSearchStatusType = CaseSearchStatus.EXISTING | CaseSearchStatus.NEW;

type NavigationDirection = 'up' | 'down';

const GlobalSearch = () => {
  const { isModalOpen, toggleModal } = MODALS_STORE.general();
  const [textSearch, setTextSearch] = useState('');
  const [searchTerm] = useDebounce(textSearch, 1000);
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedSection, setSelectedSection] = useState<CaseSearchStatus | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const existingListRefs = useRef<(HTMLLIElement | null)[]>([]);
  const newListRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [isFocused, setIsFocused] = useState(true);

  const { newCases, loadingNewCases, fetchingNewCases } = getNewCasesApi({
    searchTerm: searchTerm.trim(),
  });
  const { startCreateNewOrder } = useStartCreateNewOrder();

  const { existingCases, hasNextPage, fetchNextPage, isFetching, loadingExistingCases, isFetchingNextPage } =
    getExistingCasesApi({
      searchTerm: searchTerm.trim(),
    });

  const isSearching = loadingNewCases || loadingExistingCases || isFetching;

  // Helper function to update selection and scroll
  const updateSelection = (section: CaseSearchStatus, index: number) => {
    setSelectedSection(section);
    setSelectedIndex(index);
    scrollToItem(section, index);
  };

  // Helper function to get the first available section with items
  const getFirstAvailableSection = (): { section: CaseSearchStatus; index: number } | null => {
    const countExistingCase = existingCases?.length || 0;
    const countNewCase = newCases?.length || 0;

    if (countExistingCase > 0) {
      return { section: CaseSearchStatus.EXISTING, index: 0 };
    }
    if (countNewCase > 0) {
      return { section: CaseSearchStatus.NEW, index: 0 };
    }
    return null;
  };

  const navigateItems = (direction: NavigationDirection) => {
    const countExistingCase = existingCases?.length || 0;
    const countNewCase = newCases?.length || 0;

    // Handle initial selection (no section selected)
    if (selectedSection === null) {
      const firstAvailable = getFirstAvailableSection();
      if (firstAvailable) {
        updateSelection(firstAvailable.section, firstAvailable.index);
      }
      return;
    }

    // Navigation strategies
    const navigationStrategies = {
      down: {
        [CaseSearchStatus.EXISTING]: () => {
          // Try to move to next item in existing cases
          if (selectedIndex < countExistingCase - 1) {
            updateSelection(CaseSearchStatus.EXISTING, selectedIndex + 1);
            return;
          }
          // Move to first item in new cases if available
          if (countNewCase > 0) {
            updateSelection(CaseSearchStatus.NEW, 0);
          }
        },
        [CaseSearchStatus.NEW]: () => {
          // Try to move to next item in new cases
          if (selectedIndex < countNewCase - 1) {
            updateSelection(CaseSearchStatus.NEW, selectedIndex + 1);
          }
        },
      },
      up: {
        [CaseSearchStatus.EXISTING]: () => {
          // Try to move to previous item in existing cases
          if (selectedIndex > 0) {
            updateSelection(CaseSearchStatus.EXISTING, selectedIndex - 1);
          }
        },
        [CaseSearchStatus.NEW]: () => {
          // Try to move to previous item in new cases
          if (selectedIndex > 0) {
            updateSelection(CaseSearchStatus.NEW, selectedIndex - 1);
            return;
          }
          // Move to last item in existing cases if available
          if (countExistingCase > 0) {
            updateSelection(CaseSearchStatus.EXISTING, countExistingCase - 1);
          }
        },
      },
    };

    const strategy = navigationStrategies[direction]?.[selectedSection];
    strategy?.();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!isModalOpen(MODAL_ID.GLOBAL_SEARCH)) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        navigateItems('down');
        break;
      case 'ArrowUp':
        e.preventDefault();
        navigateItems('up');
        break;
      case 'Enter':
        if (selectedSection && selectedIndex >= 0) {
          if (selectedSection === CaseSearchStatus.EXISTING && existingCases && selectedIndex < existingCases.length) {
            handleCaseAction(existingCases[selectedIndex], CaseSearchStatus.EXISTING);
          } else if (selectedSection === CaseSearchStatus.NEW && newCases && selectedIndex < newCases.length) {
            handleCaseAction(newCases[selectedIndex], CaseSearchStatus.NEW);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        handleCloseModal();
        break;
      default:
        break;
    }
  };

  // Scroll to the selected item
  const scrollToItem = (section: CaseSearchStatus, index: number) => {
    const refs = section === CaseSearchStatus.EXISTING ? existingListRefs : newListRefs;
    if (index >= 0 && index < refs.current.length) {
      refs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  // Handle case action: view case or start new order
  const handleCaseAction = (item: IDatahubCase, status: CaseSearchStatus) => {
    if (status === CaseSearchStatus.EXISTING) {
      navigate(`${routes.v2.cases.overview}/${item.id}`);
    } else if (status === CaseSearchStatus.NEW) {
      startCreateNewOrder({
        caseId: item.id,
        caseReference: undefined,
        packageType: ORDER_CREATION_PACKAGE_TYPE.property,
      });
    }
    handleCloseModal();
  };

  // Reset selected index when search query changes
  useEffect(() => {
    setSelectedIndex(-1);
    setSelectedSection(null);
  }, [searchTerm]);

  // Initialize refs arrays when items change
  useEffect(() => {
    if (existingCases) {
      existingListRefs.current = existingListRefs.current.slice(0, existingCases.length);
    }
    if (newCases) {
      newListRefs.current = newListRefs.current.slice(0, newCases?.length || 0);
    }
  }, [existingCases, newCases]);

  // Ensure dialog maintains focus for keyboard navigation
  const maintainFocus = () => {
    if (dialogRef.current) {
      dialogRef.current.focus();
    }
  };

  function handleCloseModal() {
    toggleModal(MODAL_ID.GLOBAL_SEARCH);
    setTextSearch('');
    setSelectedIndex(-1);
    setSelectedSection(null);
  }

  return (
    <Dialog
      open={isModalOpen(MODAL_ID.GLOBAL_SEARCH)}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxHeight: '80vh',
            background: 'transparent',
            borderRadius: '1rem',
          },
        },
        transition: {
          onEntered: () => searchInputRef.current?.focus(),
        },
      }}
    >
      <Stack
        gap={1}
        onKeyDown={handleKeyDown}
        sx={{ minHeight: '60vh' }}
        ref={dialogRef}
        tabIndex={-1} // Make the div focusable but not in tab sequence
      >
        <Stack
          sx={{
            border: `1.5px solid`,
            borderColor: isFocused ? colors.primary.main : 'white',
            borderRadius: '1rem',
            background: 'white',
          }}
        >
          {/* Search Header */}
          <Box sx={{ p: 2, background: 'white', borderRadius: '1rem 1rem 0 0' }}>
            <OutlinedInput
              type={'text'}
              autoFocus
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={t('dashboard.cases.searchGlobalPlaceholder')}
              onChange={e => setTextSearch(e.target.value)}
              inputRef={searchInputRef}
              value={textSearch}
              startAdornment={<SearchIcon color="action" sx={{ color: colors.primary.main, marginRight: '0.5rem' }} />}
              endAdornment={
                textSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setTextSearch('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
              sx={{ width: '100%', border: 'none' }}
            />
          </Box>

          {/* Navigation Bar */}
          <Box
            sx={{
              px: 2,
              py: 1,
              display: { xs: 'none', sm: 'flex' },
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: colors.ruby.level10,
              borderRadius: '0 0 1rem 1rem',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                size="small"
                sx={{ background: 'white' }}
                onClick={() => {
                  navigateItems('up');
                  maintainFocus();
                }}
              >
                <ArrowUpIcon />
              </IconButton>
              <IconButton
                size="small"
                sx={{ background: 'white' }}
                type="button"
                onClick={() => {
                  navigateItems('down');
                  maintainFocus();
                }}
              >
                <ArrowDownIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body1" color="text.primary">
                {t('common.actions.close')}
              </Typography>
              <Chip label={<Typography variant="body1">Esc</Typography>} size="small" variant="outlined" />
            </Stack>
          </Box>
        </Stack>
        {/* Search Results */}

        <Box
          sx={{
            minHeight: '50vh',
            maxHeight: '80vh',
            overflow: 'auto',
            background: 'white',
            borderRadius: '1rem',
          }}
        >
          {/* Existing Cases Section */}
          <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {t('dashboard.cases.existingCases')}
            </Typography>
            {isSearching && <LinearProgress />}

            {!isEmpty(existingCases) && (
              <>
                <List disablePadding>
                  {existingCases.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <ListItem
                        ref={el => {
                          existingListRefs.current[index] = el;
                        }}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          justifyContent: { xs: 'flex-start', md: 'space-between' },
                          alignItems: { xs: 'flex-start', md: 'center' },
                          gap: 2,
                          backgroundColor:
                            selectedSection === CaseSearchStatus.EXISTING && selectedIndex === index
                              ? colors.base.level20
                              : 'transparent',
                          '&:hover': {
                            backgroundColor: colors.base.level20,
                          },
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          setSelectedSection(CaseSearchStatus.EXISTING);
                          setSelectedIndex(index);
                          maintainFocus(); // Maintain focus on the dialog
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 4, flex: 1, alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ flex: 2 }}>
                            {item.addressObj?.full ?? '-'}
                          </Typography>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {item?.reference ?? '-'}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<ArrowRightIcon />}
                          sx={{ width: 80 }}
                          onClick={e => {
                            e.stopPropagation();
                            handleCaseAction(item, CaseSearchStatus.EXISTING);
                          }}
                        >
                          {t('common.actions.view')}
                        </Button>
                      </ListItem>
                      {index < existingCases.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
                {hasNextPage && (
                  <Button
                    variant="text"
                    onClick={() => {
                      fetchNextPage();
                      maintainFocus();
                    }}
                    sx={{ color: colors.primary.main, textDecoration: 'underline' }}
                    endIcon={<CaretDownIcon />}
                    disabled={!hasNextPage || isFetchingNextPage}
                  >
                    {isFetchingNextPage ? t('common.terms.loadingMore') : t('common.actions.viewMore')}
                  </Button>
                )}
              </>
            )}

            {!loadingExistingCases && isEmpty(existingCases) && (
              <Stack justifyContent="center" alignItems="center">
                {t('common.terms.noData')}
              </Stack>
            )}
          </Box>

          <Divider />
          {/* Create New Case Section */}
          <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {t('dashboard.cases.newCases')}
            </Typography>

            {(loadingNewCases || fetchingNewCases) && <LinearProgress />}

            {newCases && newCases.length > 0 && (
              <List disablePadding>
                {newCases.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem
                      ref={el => {
                        newListRefs.current[index] = el;
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: { xs: 'flex-start', md: 'space-between' },
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 2,
                        backgroundColor:
                          selectedSection === CaseSearchStatus.NEW && selectedIndex === index
                            ? colors.base.level20
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: colors.base.level20,
                        },
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setSelectedSection(CaseSearchStatus.NEW);
                        setSelectedIndex(index);
                        maintainFocus(); // Maintain focus on the dialog
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 4, flex: 1, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ flex: 2 }}>
                          {item.addressObj?.full ?? '-'}
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {item?.reference ?? '-'}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={<ArrowRightIcon />}
                        onClick={e => {
                          e.stopPropagation();
                          handleCaseAction(item, CaseSearchStatus.NEW);
                        }}
                      >
                        {t('common.actions.start')}
                      </Button>
                    </ListItem>
                    {index < newCases.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}

            {!loadingNewCases && isEmpty(newCases) && (
              <Stack justifyContent="center" alignItems="center">
                {t('common.terms.noData')}
              </Stack>
            )}
          </Box>
        </Box>
      </Stack>
    </Dialog>
  );
};

export default GlobalSearch;
